import { useCallback, useRef, useState, useEffect } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';
import SelectableImageGrid from './SelectableImageGrid';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const HeightRestrictionReading: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const { leaseId, phiLandmarkObectDetections = [] } = data?.content || {};

  const description =
    initialDescription ||
    (data?.content
      ? `Fill out the height restriction in the same format as the image of the height restriction sign. If you can't detect the values leave as empty.`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();
  const [heightRestrictionFeet, setHeightRestrictionFeet] = useState<
    number | null
  >(null);
  const [heightRestrictionInch, setHeightRestrictionInch] = useState<
    number | null
  >(null);

  const clearAllData = () => {
    handleFocus();
    setData(undefined);
    setIsBenchmark(false);
    setHeightRestrictionFeet(null);
    setHeightRestrictionInch(null);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };
  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }
    const sending = {
      leaseId,
      outcome: outcome ?? value,
      value: JSON.stringify(
        !heightRestrictionFeet || !heightRestrictionInch
          ? { heightRestriction: null }
          : {
              heightRestriction: `${heightRestrictionFeet} FT ${heightRestrictionInch} IN`,
            },
      ),
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  useEffect(() => {
    handleFocus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onSubmit({ value: 'na' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  const feetTextBoxRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    if (feetTextBoxRef.current) feetTextBoxRef.current.focus();
  };

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <SelectableImageGrid
            images={phiLandmarkObectDetections.map(o => o.url)}
            bboxes={phiLandmarkObectDetections.map(o => o.bbox)}
            width={phiLandmarkObectDetections.length}
            height={1}
            selectable={false}
          />

          <div className="flex flex-row items-center justify-center gap-4 p-2">
            <div className="flex flex-col items-center justify-center gap-2">
              <Label>Feet</Label>
              <Input
                ref={feetTextBoxRef}
                className="w-fit"
                type="number"
                value={heightRestrictionFeet ?? ''}
                onChange={e =>
                  setHeightRestrictionFeet(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                placeholder="Feet"
              />
            </div>
            <div className="flex flex-col items-center justify-center gap-2">
              <Label>Inch</Label>
              <Input
                className="w-fit"
                type="string"
                value={heightRestrictionInch ?? ''}
                onChange={e =>
                  setHeightRestrictionInch(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                placeholder="Inch"
              />
            </div>
          </div>
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default HeightRestrictionReading;
